import {
  faArrowDownToLine,
  faClone,
  faDatabase,
  faImagePortrait,
  faLaptopFile,
  faPalette,
  faPenToSquare,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import pluralize from "pluralize";
import { SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";
import { useBoolean } from "usehooks-ts";

import { ExportDialog } from "@/components/ExportDialog";
import { getForgeSessionUrl } from "@/components/inference/utils";
import { ConfirmDialog, Icon, Menu, MenuItem } from "@/components/ui-v2";
import { ForgeIcon } from "@/components/ui-v2/icons";
import {
  useEditInCanvas,
  useRequiredWorkspace,
  useSetAsStyleCover,
  useShowSnackbar,
  useStyleActions,
  useUrqlResponseHandler,
} from "@/hooks";
import { downloadFromURL } from "@/utils/download";

import { ExportAsset } from "../../asset/queries.graphql";
import { useAvailableActionIds } from "../../files/hooks/useAvailableActionIds";
import { useDrive } from "../DriveContext";
import {
  DeleteDriveItems,
  DriveAssetFragment,
  DriveDetailedFileFragment,
  DriveDirectoryFragment,
  DriveStyleFragment,
} from "../queries.graphql";
import { DownloadOptions } from "../types";
import DriveRenameAssetDialog from "./DriveRenameAssetDialog";
import DriveRenameDirectoryDialog from "./DriveRenameDirectoryDialog";
import DriveRenameFileDialog from "./DriveRenameFileDialog";

const itemTypes = ["File", "Asset", "Directory", "Style"];

type Anchor = {
  position: {
    top: number;
    left: number;
  };
};

type DriveItemContextMenuProps = {
  anchor?: Anchor;
  onDownloadFiles: (
    files: DriveDetailedFileFragment[],
    options: DownloadOptions,
  ) => void;
  onClose: () => void;
  refreshView: () => void;
};

export const DriveItemContextMenu = ({
  anchor,
  onDownloadFiles,
  onClose,
  refreshView,
}: DriveItemContextMenuProps) => {
  const { selectedItems, currentDirectoryPath } = useDrive();
  const availableActionIds = useAvailableActionIds({
    objects: selectedItems,
    pageContext: "drive",
  });

  const { workspace } = useRequiredWorkspace();
  const {
    value: isDeleteDialogOpen,
    setTrue: openDeleteDialog,
    setFalse: closeDeleteDialog,
  } = useBoolean();
  const {
    value: isStyleCoverImageConfirmDialogOpen,
    setTrue: openStyleCoverImageConfirmDialog,
    setFalse: closeStyleCoverImageConfirmDialog,
  } = useBoolean();
  const {
    value: isExportDialogOpen,
    setTrue: openExportDialog,
    setFalse: closeExportDialog,
  } = useBoolean();
  const [fileToRename, setFileToRename] =
    useState<DriveDetailedFileFragment | null>(null);
  const [assetToRename, setAssetToRename] = useState<DriveAssetFragment | null>(
    null,
  );
  const [directoryToRename, setDirectoryToRename] =
    useState<DriveDirectoryFragment | null>(null);

  const navigate = useNavigate();
  const { showError } = useShowSnackbar();

  const { getResponseFromState } = useUrqlResponseHandler();

  const { createStyle, navigateToForgeWithStyle } = useStyleActions();

  const [, deleteDriveItems] = useMutation(DeleteDriveItems);
  const [, exportAsset] = useMutation(ExportAsset);
  const { setAsStyleCover } = useSetAsStyleCover();
  const { editInCanvas } = useEditInCanvas();

  const handleClickRenameFile = () => {
    const item = selectedItems[0] as DriveDetailedFileFragment;
    setFileToRename(item);
    onClose();
  };

  const handleClickRenameAsset = () => {
    const item = selectedItems[0] as DriveAssetFragment;
    setAssetToRename(item);
    onClose();
  };

  const handleClickRenameDirectory = () => {
    const item = selectedItems[0] as DriveDirectoryFragment;
    setDirectoryToRename(item);
    onClose();
  };

  const handleClickForgeWithStyle = () => {
    const item = selectedItems[0] as DriveStyleFragment;
    onClose();
    navigateToForgeWithStyle(
      workspace.name,
      item.id,
      item.primaryInferenceMode,
    );
  };

  const handleClickDuplicateStyle = async () => {
    const item = selectedItems[0] as DriveStyleFragment;
    const { style } = await createStyle.execute({
      workspaceId: workspace.id,
      directory: currentDirectoryPath,
      duplicateFromStyleId: item.id,
    });
    if (style) {
      refreshView();
      window.open(`${style.path}/create`);
    }
    onClose();
  };

  const handleClickNewStyleFromImages = async () => {
    const inputFiles = selectedItems
      .filter((i) => i.__typename === "File")
      .map((item) => ({
        fileId: item.id,
      }));

    const { style } = await createStyle.execute({
      workspaceId: workspace.id,
      directory: currentDirectoryPath,
      inputFiles,
      trainingConfig: {
        method: "TRAINED_LORA",
        useImageCaptions: true,
      },
    });
    if (style) {
      navigate(`/${workspace.name}/models/${style.slug}/create`);
    }
    onClose();
  };

  const handleClickNewStyleFromModelFile = async () => {
    const inputFiles = selectedItems
      .filter((i) => i.__typename === "File")
      .map((item) => ({
        fileId: item.id,
      }));

    const { style } = await createStyle.execute({
      workspaceId: workspace.id,
      directory: currentDirectoryPath,
      inputFiles,
      trainingConfig: {
        method: "UPLOADED_LORA",
      },
    });
    if (style) {
      navigate(`/${workspace.name}/models/${style.slug}/create`);
    }
    onClose();
  };

  const handleClickEditInCanvas = async () => {
    onClose();

    if (
      selectedItems[0].__typename === "Asset" ||
      selectedItems[0].__typename === "File"
    ) {
      await editInCanvas({
        object: selectedItems[0],
      });
    }
  };

  const handleClickOpenInForgeSession = () => {
    const item = selectedItems[0] as DriveDetailedFileFragment;
    const sessionId = item.inference?.session?.id;
    if (sessionId) {
      const forgeSessionURL = getForgeSessionUrl({
        workspaceName: workspace.name,
        sessionId,
      });
      navigate(forgeSessionURL);
    } else {
      showError("Error loading associated Forge Session");
    }
    onClose();
  };

  const handleClickDownloadFile = (event: SyntheticEvent) => {
    onClose();
    event.stopPropagation();
    event.preventDefault();
    onDownloadFiles(selectedItems as DriveDetailedFileFragment[], {
      isExport: true,
    });
  };

  const handleClickDownloadAs = (event: SyntheticEvent) => {
    onClose();
    event.stopPropagation();
    event.preventDefault();
    openExportDialog();
  };

  const handleClickDownloadAsset = async (event: SyntheticEvent) => {
    onClose();
    event.stopPropagation();
    event.preventDefault();
    const assets = selectedItems as DriveAssetFragment[];
    if (assets.length !== 1) {
      return;
    }
    const asset = assets[0];
    const response = await exportAsset({
      exportAssetInput: {
        assetId: asset.id,
        format: "PSD",
      },
    });

    const {
      data: { exportAsset: exportedAsset },
    } = getResponseFromState({ state: response });
    if (exportedAsset) {
      downloadFromURL([
        { name: `${asset.name || "asset"}.psd`, url: exportedAsset.url },
      ]);
    }
  };

  const handleClickDelete = () => {
    onClose();
    openDeleteDialog();
  };

  const handleConfirmDelete = async () => {
    const response = await deleteDriveItems({
      deleteDriveItemsInput: {
        workspaceId: workspace.id,
        fileIds: selectedItems
          .filter((i) => i.__typename === "File")
          .map((item) => item.id),
        assetIds: selectedItems
          .filter((i) => i.__typename === "Asset")
          .map((item) => item.id),
        directoryIds: selectedItems
          .filter((i) => i.__typename === "Directory")
          .map((item) => item.id),
        styleIds: selectedItems
          .filter(
            (i) => i.__typename === "Style" || i.__typename === "ReferenceSet",
          )
          .map((item) => item.id),
      },
    });

    const { error } = getResponseFromState({ state: response });

    if (!error) {
      refreshView();
    }
  };

  const handleClickSetAsCoverImage = () => {
    onClose();
    openStyleCoverImageConfirmDialog();
  };

  const handleConfirmSetAsCoverImage = () => {
    const item = selectedItems[0] as DriveDetailedFileFragment;

    if (item && item.styles && item.styles.length) {
      setAsStyleCover(item.styles[0], item);
    }
  };

  const counts = selectedItems?.reduce(
    (acc, item) => {
      acc[item.__typename] = (acc[item.__typename] || 0) + 1;
      return acc;
    },
    {} as { [key: string]: number },
  );

  // String name to display for actions (e.g. "3 Models, 1 Image, and 2 Styles")
  const countsString = itemTypes
    .map((key) => {
      return { key, count: counts[key] };
    })
    .filter((item) => item.count && item.count > 0)
    .reduce((acc, item, index, array) => {
      if (index > 0) {
        if (index === array.length - 1) {
          acc += " and ";
        } else {
          acc += ", ";
        }
      }
      let countString = `${item.count} `;
      if (selectedItems.length === 1) {
        countString = "";
      }
      return `${acc}${countString}${pluralize(
        item.key.toLowerCase(),
        item.count,
      )}`;
    }, "");

  const getStyleName = () => {
    const item = selectedItems[0];

    if (item?.__typename === "File" && item.styles.length) {
      return item.styles[0].name;
    }

    return "model";
  };

  return (
    // Prevent event propagation to be captured by the onBodyClick or onBodyContextMenu
    <div
      onContextMenu={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Menu
        open={!!anchor}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={anchor?.position}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "180px",
          },
        }}
      >
        {availableActionIds.has("RENAME_DIRECTORY") && (
          <MenuItem
            onClick={handleClickRenameDirectory}
            icon={<Icon icon={faPenToSquare} />}
          >
            Rename Directory
          </MenuItem>
        )}
        {availableActionIds.has("FORGE_WITH_STYLE") && (
          <MenuItem onClick={handleClickForgeWithStyle} icon={<ForgeIcon />}>
            Forge with Model
          </MenuItem>
        )}
        {availableActionIds.has("DUPLICATE_STYLE") && (
          <MenuItem
            onClick={handleClickDuplicateStyle}
            icon={<Icon icon={faClone} />}
          >
            Duplicate Model
          </MenuItem>
        )}

        {availableActionIds.has("RENAME_FILE") && (
          <MenuItem
            onClick={handleClickRenameFile}
            icon={<Icon icon={faPenToSquare} />}
          >
            Rename File
          </MenuItem>
        )}

        {availableActionIds.has("EDIT") && (
          <MenuItem
            onClick={handleClickEditInCanvas}
            icon={<Icon icon={faPenToSquare} />}
          >
            Edit in Canvas
          </MenuItem>
        )}

        {availableActionIds.has("OPEN_IN_SESSION") && (
          <MenuItem
            onClick={handleClickOpenInForgeSession}
            icon={<Icon icon={faDatabase} />}
          >
            Open in Forge Session
          </MenuItem>
        )}

        {availableActionIds.has("DOWNLOAD") && (
          <MenuItem
            onClick={handleClickDownloadFile}
            icon={<Icon icon={faArrowDownToLine} />}
          >
            Quick Download
          </MenuItem>
        )}

        {availableActionIds.has("DOWNLOAD_AS") && (
          <MenuItem
            onClick={handleClickDownloadAs}
            icon={<Icon icon={faLaptopFile} />}
          >
            Download as...
          </MenuItem>
        )}

        {availableActionIds.has("CREATE_STYLE_FROM_IMAGES") && (
          <MenuItem
            onClick={handleClickNewStyleFromImages}
            icon={<Icon icon={faPalette} />}
          >
            Create model from {countsString}
          </MenuItem>
        )}
        {availableActionIds.has("CREATE_STYLE_FROM_MODEL") && (
          <MenuItem
            onClick={handleClickNewStyleFromModelFile}
            icon={<Icon icon={faPalette} />}
          >
            Create Model from File
          </MenuItem>
        )}
        {availableActionIds.has("RENAME_ASSET") && (
          <MenuItem
            onClick={handleClickRenameAsset}
            icon={<Icon icon={faPenToSquare} />}
          >
            Rename Asset
          </MenuItem>
        )}

        {availableActionIds.has("DOWNLOAD_ASSET") && (
          <MenuItem
            onClick={handleClickDownloadAsset}
            icon={<Icon icon={faArrowDownToLine} />}
          >
            Download Asset
          </MenuItem>
        )}

        {availableActionIds.has("SET_AS_COVER_IMAGE") && (
          <MenuItem
            onClick={handleClickSetAsCoverImage}
            icon={<Icon icon={faImagePortrait} />}
          >
            Set as Model Cover Image
          </MenuItem>
        )}

        {availableActionIds.has("DELETE") && (
          <MenuItem
            onClick={handleClickDelete}
            icon={<Icon icon={faTrashCan} />}
          >
            Delete {countsString}
          </MenuItem>
        )}
      </Menu>
      {fileToRename && (
        <DriveRenameFileDialog
          isOpen={!!fileToRename}
          onClose={() => setFileToRename(null)}
          file={fileToRename}
          refreshView={refreshView}
        />
      )}
      {assetToRename && (
        <DriveRenameAssetDialog
          isOpen={!!assetToRename}
          onClose={() => setAssetToRename(null)}
          asset={assetToRename}
          refreshView={refreshView}
        />
      )}
      {directoryToRename && (
        <DriveRenameDirectoryDialog
          isOpen={!!directoryToRename}
          onClose={() => setDirectoryToRename(null)}
          directory={directoryToRename}
          refreshView={refreshView}
        />
      )}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        title={`Delete ${countsString || "items"}?`}
        description={`Are you sure you want to delete ${
          countsString || "these items"
        }?`}
        onConfirm={() => {
          closeDeleteDialog();
          handleConfirmDelete();
        }}
        onClose={() => {
          closeDeleteDialog();
        }}
      />
      <ConfirmDialog
        open={isStyleCoverImageConfirmDialogOpen}
        title="Set as Model Cover Image"
        description={`Are you sure you want to set this image as the ${getStyleName()} cover image?`}
        onConfirm={() => {
          closeStyleCoverImageConfirmDialog();
          handleConfirmSetAsCoverImage();
        }}
        onClose={() => {
          closeStyleCoverImageConfirmDialog();
        }}
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={closeExportDialog}
        objects={selectedItems}
      />
    </div>
  );
};
